import React, { useState, useRef } from "react";
import { Button } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Spinner from "../../shared/components/Spinner/Spinner";
import emailjs from "@emailjs/browser";
import skfLogo from "../RequestTutor/assets/skf-logo.webp";

const RequestTutor = () => {
  const [course, setCourse] = useState("");
  const [studentName, setStudentName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [email, setEmail] = useState("");
  const [inquiry, setInquiry] = useState("");

  const [isInvalidCourse, setIsInvalidCourse] = useState(false);
  const [isInvalidName, setIsInvalidName] = useState(false);
  const [isInvalidContact, setIsInvalidContact] = useState(false);
  const [isInvalidEmail, setIsInvalidEmail] = useState(false);
  const [isInvalidInquiry, setIsInvalidInquiry] = useState(false);

  const [invalidCourseMsg, setInvalidCourseMsg] = useState("");
  const [invalidNameMsg, setInvalidNameMsg] = useState("");
  const [invalidContactMsg, setInvalidContactMsg] = useState("");
  const [invalidEmailMsg, setInvalidEmailMsg] = useState("");
  const [invalidInquiryMsg, setInvalidInquiryMsg] = useState("");

  const [show, setShow] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);

  const handleClose = () => setShow(false);

  const validate = () => {
    let isValid = true;
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    if (!course) {
      setIsInvalidCourse(true);
      setInvalidCourseMsg("Please select a course");
      isValid = false;
    } else {
      setIsInvalidCourse(false);
      setInvalidCourseMsg("");
    }

    if (!studentName || studentName.length < 3) {
      setIsInvalidName(true);
      setInvalidNameMsg(
        studentName
          ? "Name should have at least 3 characters"
          : "Student name is required",
      );
      isValid = false;
    } else {
      setIsInvalidName(false);
      setInvalidNameMsg("");
    }

    if (!contactNumber || contactNumber.length < 8) {
      setIsInvalidContact(true);
      setInvalidContactMsg(
        contactNumber
          ? "Contact number should be valid"
          : "Contact number is required",
      );
      isValid = false;
    } else {
      setIsInvalidContact(false);
      setInvalidContactMsg("");
    }

    if (!email || !emailRegex.test(email)) {
      setIsInvalidEmail(true);
      setInvalidEmailMsg(email ? "Invalid email" : "Email is required");
      isValid = false;
    } else {
      setIsInvalidEmail(false);
      setInvalidEmailMsg("");
    }

    if (!inquiry || inquiry.length < 10) {
      setIsInvalidInquiry(true);
      setInvalidInquiryMsg(
        inquiry
          ? "Inquiry should be at least 10 characters"
          : "Inquiry is required",
      );
      isValid = false;
    } else {
      setIsInvalidInquiry(false);
      setInvalidInquiryMsg("");
    }

    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validate() && formRef.current) {
      setShowSpinner(true);
      emailjs
        .sendForm(
          process.env.REACT_APP_EMAILJS_SERVICE_ID!,
          process.env.REACT_APP_EMAILJS_TEMPLATE_ID!,
          formRef.current,
          process.env.REACT_APP_EMAILJS_PUBLIC_KEY!,
        )
        .then(() => {
          setShowSpinner(false);
          setShow(true);
          setCourse("");
          setStudentName("");
          setContactNumber("");
          setEmail("");
          setInquiry("");
        })
        .catch((error) => {
          setShowSpinner(false);
          console.error("Email send error:", error);
        });
    }
  };

  return (
    <div
      className="d-flex justify-content-center align-items-center bg-light"
      style={{ minHeight: "100vh", padding: "20px" }}
    >
      <div
        className="p-4 shadow rounded bg-white text-center"
        style={{ maxWidth: "600px", width: "100%" }}
      >
        <img src={skfLogo} width={200} className="mb-3" alt="Skill Surf Logo" />
        <h3 className="text-center mb-3" style={{ fontWeight: 600 }}>
          Request a Tutor
        </h3>
        <p className="text-center text-muted mb-4">
          Need help with a specific course? Fill out the form below, and we'll
          connect you with a tutor!
        </p>

        <Form ref={formRef} onSubmit={handleSubmit}>
          {/* Course Dropdown */}
          <Form.Group className="mb-3">
            <Form.Select
              name="course_name"
              value={course}
              onChange={(e) => setCourse(e.target.value)}
            >
              <option value="">Select Course</option>
              <option value="CMOS Analog IC Design and Simulation '25">
                CMOS Analog IC Design and Simulation '25
              </option>
              <option value="Cryptography Fundamentals: Securing the Digital World">
                Cryptography Fundamentals: Securing the Digital World
              </option>
              <option value="Embedded Machine Learning for Edge Computing '24">
                Embedded Machine Learning for Edge Computing '24
              </option>
              <option value="Embedded Product Design for IoT '25">
                Embedded Product Design for IoT '25
              </option>
              <option value="Fundamentals of Digital System Design '24">
                Fundamentals of Digital System Design '24
              </option>
              <option value="Microcontroller Based Embedded System Design '24">
                Microcontroller Based Embedded System Design '24
              </option>
              <option value="System Verilog for ASIC/FPGA Design & Simulation '25">
                System Verilog for ASIC/FPGA Design & Simulation '25
              </option>
            </Form.Select>
            {isInvalidCourse && (
              <p className="text-danger">{invalidCourseMsg}</p>
            )}
          </Form.Group>

          {/* Student Name */}
          <Form.Group className="mb-3">
            <Form.Control
              type="text"
              placeholder="Your Name"
              name="student_name"
              value={studentName}
              onChange={(e) => setStudentName(e.target.value)}
            />
            {isInvalidName && <p className="text-danger">{invalidNameMsg}</p>}
          </Form.Group>

          {/* Contact Number */}
          <Form.Group className="mb-3">
            <Form.Control
              type="text"
              placeholder="Contact Number (WhatsApp preferred)"
              name="contact_number"
              value={contactNumber}
              onChange={(e) => setContactNumber(e.target.value)}
            />
            {isInvalidContact && (
              <p className="text-danger">{invalidContactMsg}</p>
            )}
          </Form.Group>

          {/* Email */}
          <Form.Group className="mb-3">
            <Form.Control
              type="email"
              placeholder="Your Email"
              name="email_address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {isInvalidEmail && <p className="text-danger">{invalidEmailMsg}</p>}
          </Form.Group>

          {/* Inquiry */}
          <Form.Group className="mb-3">
            <Form.Control
              as="textarea"
              rows={6}
              placeholder="Your Inquiry"
              name="inquiry"
              value={inquiry}
              onChange={(e) => setInquiry(e.target.value)}
            />
            {isInvalidInquiry && (
              <p className="text-danger">{invalidInquiryMsg}</p>
            )}
          </Form.Group>

          <Button
            className="w-100 text-white"
            variant="primary"
            type="submit"
            style={{ fontWeight: 500 }}
          >
            Submit Request
          </Button>
        </Form>
      </div>

      {/* Success Modal */}
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Body className="d-flex flex-column">
          <p className="text-gray mb-0">
            Thank you! Your tutor request has been sent. We'll get back to you
            soon.
          </p>
          <div className="d-flex justify-content-end mt-4">
            <Button variant="secondary" onClick={handleClose}>
              Okay
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Spinner showSpinner={showSpinner} />
    </div>
  );
};

export default RequestTutor;
